import React, { useState } from "react"
import { asDialogData } from "../../../core/utils/utils"
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import {
    useGetAllExperienceLevelsQuery,
    useGetActiveSeasonQuery,
    useCreateExperienceLevelMutation,
    useUpdateExperienceLevelMutation,
    useDeleteExperienceLevelMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"

export default function ExperienceLevelsAdmin() {
    // Уровни всех сезонов (пользовательский /levels отдаёт только активный сезон).
    const { data: levels = [], isLoading } = useGetAllExperienceLevelsQuery()
    const { data: activeSeason } = useGetActiveSeasonQuery()
    const [createLvl, createResp] = useCreateExperienceLevelMutation()
    const [updateLvl, updateResp] = useUpdateExperienceLevelMutation()
    const [deleteLvl] = useDeleteExperienceLevelMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ level: 1, minXp: 100, rewardBelcoins: 1, season: "" })
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openNew() {
        setEditing({})
        // Префилл следующего уровня считаем только по сетке активного сезона;
        // пока сезон не загрузился — не угадываем.
        const seasonLevels = activeSeason ? levels.filter(l => l.season === activeSeason.number) : []
        const nextLvl = seasonLevels.length ? Math.max(...seasonLevels.map(l => l.level)) + 1 : 1
        setForm({ level: nextLvl, minXp: nextLvl * 100, rewardBelcoins: 1, season: activeSeason?.number ?? "" })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({ level: row.level, minXp: row.minXp, rewardBelcoins: row.rewardBelcoins, season: row.season ?? "" })
    }
    function close() { setEditing(null) }

    async function save() {
        const body = {
            level: parseInt(form.level, 10),
            minXp: parseInt(form.minXp, 10),
            rewardBelcoins: parseInt(form.rewardBelcoins, 10),
        }
        // Сезон задаётся только при создании; пустое поле → активный сезон.
        if (!editing.id && form.season !== "") {
            body.season = parseInt(form.season, 10)
        }
        const resp = editing.id
            ? await updateLvl({ id: editing.id, ...body })
            : await createLvl(body)
        setActionResp(asDialogData(resp)); setRespOpen(true); close()
    }
    async function remove(row) {
        if (!window.confirm(`Delete level ${row.level}?`)) return
        const resp = await deleteLvl(row.id)
        setActionResp(asDialogData(resp)); setRespOpen(true)
    }

    const columns = [
        { field: "season", headerName: "Season", width: 90, renderCell: (p) => p.value ?? "—" },
        { field: "level", headerName: "Level", width: 110 },
        { field: "minXp", headerName: "Min XP", width: 120 },
        { field: "rewardBelcoins", headerName: "Reward BC", width: 180 },
        { field: "actions", headerName: "", width: 100, renderCell: (p) =>
            <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(p.row)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
        },
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">XP levels</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                    New level
                </Button>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                Invariants: `level` is unique, `minXp` grows monotonically (enforced by backend). Changes apply to new requests only — accumulated XP is not recalculated.
            </Alert>
            <Paper sx={{ height: 480 }}>
                <DataGrid
                    rows={levels}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="xs" fullWidth>
                <DialogTitle>{editing?.id ? `Level ${editing.level}` : "New level"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Level" type="number" value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} />
                        <TextField label="Min XP" type="number" value={form.minXp} onChange={e => setForm({ ...form, minXp: e.target.value })} />
                        <TextField label="Reward (BC)" type="number" value={form.rewardBelcoins} onChange={e => setForm({ ...form, rewardBelcoins: e.target.value })} />
                        <TextField
                            label="Season"
                            type="number"
                            helperText={editing?.id ? "Сезон записи менять нельзя" : "Пусто — активный сезон; будущий номер — подготовка сетки заранее"}
                            value={form.season}
                            onChange={e => setForm({ ...form, season: e.target.value })}
                            disabled={!!editing?.id}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save} disabled={createResp.isLoading || updateResp.isLoading}>Save</Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Level"
                information="Saved"
            />
        </Box>
    )
}
